import { useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { fetcher, postFetcher } from '@/hooks'
import { cn } from '@/lib/utils'

const warningMessages = {
  PARSE_FALLBACK:
    'The content is not valid JSON. Unit IDs were extracted with text matching.',
  NO_UNIT_ID_FOUND: 'No AdMob unit ID was found in the pasted content.',
}

function RcAdmobManualCheckDialog({ open, onOpenChange }) {
  const [productKey, setProductKey] = useState('')
  const [content, setContent] = useState('')
  const [result, setResult] = useState(null)
  const [submitError, setSubmitError] = useState('')

  const {
    data: apps,
    error: appsError,
    isLoading: isAppsLoading,
  } = useSWR(
    open ? ['/admob/ad-pos-conf-audit/apps'] : null,
    fetcher,
    { revalidateOnFocus: false },
  )
  const { trigger, isMutating } = useSWRMutation(
    '/admob/ad-pos-conf-audit/manual-check',
    postFetcher,
  )

  const reset = () => {
    setProductKey('')
    setContent('')
    setResult(null)
    setSubmitError('')
  }

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      reset()
    }
    onOpenChange(nextOpen)
  }

  const handleSubmit = async () => {
    if (!productKey || !content.trim() || isMutating) return

    setSubmitError('')
    setResult(null)
    try {
      const nextResult = await trigger({ productKey, content })
      setResult(nextResult)
    } catch (error) {
      setSubmitError(error?.message || 'Manual ad_pos_conf check failed')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manual ad_pos_conf Check</DialogTitle>
          <DialogDescription>
            Select the target app, paste the complete ad_pos_conf value, and
            verify every AdMob unit ID against that app.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-2">
          <div className="space-y-2">
            <Label htmlFor="manual-rc-app">App</Label>
            <Select
              value={productKey}
              onValueChange={(value) => {
                setProductKey(value)
                setResult(null)
                setSubmitError('')
              }}
              disabled={isAppsLoading || Boolean(appsError)}
            >
              <SelectTrigger id="manual-rc-app">
                <SelectValue
                  placeholder={
                    isAppsLoading ? 'Loading apps...' : 'Select an app'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Audited apps</SelectLabel>
                  {(apps || []).map((app) => (
                    <SelectItem key={app.productKey} value={app.productKey}>
                      {app.product} · {app.packageName}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="manual-rc-content">ad_pos_conf content</Label>
            <Textarea
              id="manual-rc-content"
              value={content}
              onChange={(event) => {
                setContent(event.target.value)
                setResult(null)
                setSubmitError('')
              }}
              placeholder='Paste the complete value, for example: {"banner":"ca-app-pub-.../..."}'
              className="min-h-56 font-mono text-xs"
              disabled={isMutating}
            />
          </div>

          {appsError && (
            <Alert variant="destructive">
              <AlertTitle>Unable to load apps</AlertTitle>
              <AlertDescription>
                {appsError.message || 'The app list request failed.'}
              </AlertDescription>
            </Alert>
          )}

          {submitError && (
            <Alert variant="destructive">
              <AlertTitle>Check failed</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {result && (
            <div className="space-y-3">
              <Alert
                variant={
                  result.status === 'INVALID_UNIT_ID'
                    ? 'destructive'
                    : 'default'
                }
                className={cn(
                  result.status === 'PASS' &&
                    'border-green-500/50 text-green-700',
                )}
              >
                <AlertTitle>
                  {result.status === 'PASS'
                    ? 'All unit IDs passed'
                    : result.status === 'INVALID_UNIT_ID'
                      ? 'Invalid unit IDs found'
                      : 'No unit IDs found'}
                </AlertTitle>
                <AlertDescription>
                  {result.totalUnitIds} total · {result.passCount} passed ·{' '}
                  {result.invalidCount} invalid
                </AlertDescription>
              </Alert>

              {result.warnings?.map((warning) => (
                <Alert key={warning}>
                  <AlertDescription>
                    {warningMessages[warning] || warning}
                  </AlertDescription>
                </Alert>
              ))}

              {result.rows.length > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Unit ID
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Result
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row) => (
                        <tr key={row.unitId} className="border-t">
                          <td className="break-all px-3 py-2 font-mono text-xs">
                            {row.unitId}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-2 font-medium',
                              row.result === 'PASS'
                                ? 'text-green-700'
                                : 'text-destructive',
                            )}
                          >
                            {row.result}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isMutating}
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isMutating ||
              !productKey ||
              !content.trim() ||
              Boolean(appsError)
            }
          >
            {isMutating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isMutating ? 'Checking...' : 'Run Check'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RcAdmobManualCheckDialog
